import { queryOptions, useQuery } from "@tanstack/react-query";
import productsApi from "../apis/productsApi";
import { useAuthenticated } from "../hooks/authHooks";
import type { AnalysisGroup, AnalysisType } from "../models/analysisGroup";
import type { AnalysisMethod } from "../models/analysisMethod";
import type { IProduct } from "../models/product";
import type { IProductFamily } from "../models/productFamily";
import type { ProductPackage } from "../models/productPackage";

const productKeys = {
  all: ["products"] as const,
  families: () => [...productKeys.all, "families"] as const,
  products: () => [...productKeys.all, "list"] as const,
  packages: () => [...productKeys.all, "packages"] as const,
  analysisGroups: () => [...productKeys.all, "analysisGroups"] as const,
  groupedAnalysisGroups: () =>
    [...productKeys.all, "groupedAnalysisGroups"] as const,
  analysisTypes: () => [...productKeys.all, "analysisTypes"] as const,
  analysisMethods: () => [...productKeys.all, "analysisMethods"] as const,
};
const staleTime = Number.POSITIVE_INFINITY;
export const useProductFamiliesQuery = () => {
  return useQuery<IProductFamily[]>({
    queryKey: productKeys.families(),
    queryFn: () => productsApi.getProductFamilies(),
    enabled: useAuthenticated(),
    staleTime,
  });
};

export const useProductsQuery = () => {
  return useQuery<IProduct[]>({
    queryKey: productKeys.products(),
    queryFn: () => productsApi.getProducts(),
    enabled: useAuthenticated(),
    staleTime,
  });
};

export const useProductPackagesQuery = () => {
  return useQuery<{ [key: number]: ProductPackage[] }>({
    queryKey: productKeys.packages(),
    queryFn: () => productsApi.getProductPackages(),
    enabled: useAuthenticated(),
    staleTime,
  });
};

export const analysisGroupsQueryOptions = queryOptions<AnalysisGroup[]>({
  queryKey: productKeys.analysisGroups(),
  queryFn: () => productsApi.getAnalysisGroups(),
  staleTime,
});

export const analysisTypesQueryOptions = queryOptions<AnalysisType[]>({
  queryKey: productKeys.analysisTypes(),
  queryFn: () => productsApi.getAnalysisTypes(),
  staleTime,
});

export const analysisMethodsQueryOptions = queryOptions<AnalysisMethod[]>({
  queryKey: productKeys.analysisMethods(),
  queryFn: () => productsApi.getAnalysisMethods(),
  staleTime,
});

export const useAnalysisMethodsQuery = () => {
  return useQuery({
    ...analysisMethodsQueryOptions,
    enabled: useAuthenticated(),
  });
};

export const useAnalysisGroupsQuery = () => {
  return useQuery({
    ...analysisGroupsQueryOptions,
    enabled: useAuthenticated(),
  });
};

export const useGroupedAnalysisGroupsQuery = () => {
  return useQuery<{ [key: number]: AnalysisGroup[] }>({
    queryKey: productKeys.groupedAnalysisGroups(),
    queryFn: () => productsApi.getProductFamilyAnalyses(),
    enabled: useAuthenticated(),
    staleTime,
  });
};

export const useAnalysisTypesQuery = () => {
  return useQuery({
    ...analysisTypesQueryOptions,
    enabled: useAuthenticated(),
  });
};
